import { useStore } from 'jotai';
import { useCallback } from 'react';
import {
  sidebarStackAtom,
  type SidebarStackItem,
} from '@/common/sidebar/sidebar-store.ts';
import {
  getSidebarStackItem,
  type SidebarId,
  type SidebarParams,
} from '@/common/sidebar/sidebar-registry.tsx';
import { deepEqual } from 'fast-equals';
import useSidebarSearchState from '@/common/sidebar/useSidebarSearchState.ts';

export default function useSidebarStack() {
  const store = useStore();
  const search = useSidebarSearchState();

  const syncSearchState = useCallback(
    (nextStack: SidebarStackItem[]) => {
      const currentItem = nextStack.at(-1);

      search.set(
        currentItem
          ? {
              sidebarId: currentItem.id,
              sidebarParams: JSON.stringify(currentItem.params),
            }
          : {
              sidebarId: undefined,
              sidebarParams: undefined,
            },
      );
    },
    [search],
  );

  const getStack = useCallback(() => {
    return store.get(sidebarStackAtom);
  }, [store]);

  const pushItem = useCallback(
    <TId extends SidebarId>(id: TId, params: SidebarParams<TId>) => {
      const newItem = getSidebarStackItem(id, params);
      if (newItem === null) return;

      const stack = store.get(sidebarStackAtom);

      const currentItem = stack.at(-1);
      if (currentItem !== undefined) {
        if (deepEqual(currentItem, newItem)) {
          return;
        }
      }

      const matchingItemIndex = stack.findIndex((item) =>
        deepEqual(item, newItem),
      );
      if (matchingItemIndex !== -1) {
        const newStack = [
          ...stack.filter((_item, index) => index !== matchingItemIndex),
          newItem,
        ];
        store.set(sidebarStackAtom, newStack);
        syncSearchState(newStack);
        return;
      }

      const newStack = [...stack, newItem];
      store.set(sidebarStackAtom, newStack);
      syncSearchState(newStack);
    },
    [store, syncSearchState],
  );

  const pushItemRaw = useCallback(
    (id: string, params: Record<string, unknown>) => {
      pushItem(id as SidebarId, params as SidebarParams<never>);
    },
    [store],
  );

  const replaceItem = useCallback(
    <TId extends SidebarId>(id: TId, params: SidebarParams<TId>) => {
      const newItem = getSidebarStackItem(id, params);
      if (newItem === null) return;

      const stack = store.get(sidebarStackAtom);

      const currentItem = stack.at(-1);
      if (currentItem !== undefined) {
        if (deepEqual(currentItem, newItem)) {
          return;
        }
      }

      const newStack = [...stack.slice(0, -1)];

      const matchingItemIndex = stack.findIndex((item) =>
        deepEqual(item, newItem),
      );
      if (matchingItemIndex !== -1) {
        const filteredStack = [
          ...newStack.filter((_item, index) => index !== matchingItemIndex),
          newItem,
        ];
        store.set(sidebarStackAtom, filteredStack);
        syncSearchState(filteredStack);
        return;
      }

      const nextStack = [...newStack, newItem];
      store.set(sidebarStackAtom, nextStack);
      syncSearchState(nextStack);
    },
    [store, syncSearchState],
  );

  const popItem = useCallback(() => {
    const stack = store.get(sidebarStackAtom);
    const currentItem = stack.at(-1);
    if (currentItem !== undefined) {
      const nextStack = [...stack.slice(0, -1)];
      store.set(sidebarStackAtom, nextStack);
      syncSearchState(nextStack);
    }
    return currentItem;
  }, [store, syncSearchState]);

  const surfaceItem = useCallback(
    (existingItem: SidebarStackItem) => {
      const stack = store.get(sidebarStackAtom);

      const matchingItemIndex = stack.findIndex((item) =>
        deepEqual(existingItem, item),
      );
      if (matchingItemIndex !== -1) {
        const newStack = [
          ...stack.filter((_item, index) => index !== matchingItemIndex),
          existingItem,
        ];
        store.set(sidebarStackAtom, newStack);
        syncSearchState(newStack);
      }
    },
    [store, syncSearchState],
  );

  const popBackTo = useCallback(
    (existingItem: SidebarStackItem) => {
      const stack = store.get(sidebarStackAtom);

      const matchingItemIndex = stack.findIndex((item) =>
        deepEqual(existingItem, item),
      );
      if (matchingItemIndex !== -1) {
        const nextStack = [...stack.slice(0, matchingItemIndex + 1)];
        store.set(sidebarStackAtom, nextStack);
        syncSearchState(nextStack);
      }
    },
    [store, syncSearchState],
  );

  return {
    getStack,
    pushItem,
    pushItemRaw,
    replaceItem,
    popItem,
    surfaceItem,
    popBackTo,
  };
}
